import React, { FC, useMemo, memo } from 'react';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';
import { ReactComponent as ArrowIcon } from '@blink/assets/src/icons/arrowBtn.svg';

import { AssetsToLendItemRow } from './AssetsToLendItemRow';

import { useHasAgreementError } from '@/shared/hooks/useErrors';
import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { Button, ButtonVariant, SkeletonRow } from '@/shared/ui';
import { ROLE } from '@/shared/api/borrow';
import { useHandleNavigation } from '@/features/Pools/hooks/useHandleNavigation';
import { useFetchAgreements } from '@/shared/hooks/useFetchAgreements';
import { ServerError } from '@/widgets/ServerError';
import { getAgreementsAction } from '@/shared/store/agreements/actions';

export const AssetsToLendSection: FC = memo(() => {
    const { agreements, agreementsLoading } = useFetchAgreements(ROLE.LENDER);
    const handleClick = useHandleNavigation();
    const hasError = useHasAgreementError();

    const lenderAgreements = useMemo(
        () => agreements.filter((item) => item.role === ROLE.LENDER),
        [agreements],
    );

    const isEmpty = useMemo(
        () => lenderAgreements.length === 0,
        [lenderAgreements, agreementsLoading],
    );

    const getContent = () => {
        if (agreementsLoading && isEmpty) {
            return Array(6).fill(<SkeletonRow />);
        }

        return lenderAgreements.map((item, index) => (
            <AssetsToLendItemRow
                key={index}
                itemIndex={index}
                allItems={lenderAgreements}
                role={ROLE.LENDER}
            />
        ));
    };

    let content;

    if (hasError) {
        content = <ServerError action={getAgreementsAction} />;
    } else if (isEmpty) {
        content = (
            <EmptyBanner
                customTitle='You don’t have any whitelisted pools yet.'
                customSubtitle='Please check back later or contact support if you need help with accessing pools.'
                icon={ClockIcon}
            />
        );
    } else {
        content = (
            <div className='overflow-x-auto'>
                <table className='w-full border-collapse border border-opaque min-w-[950px]'>
                    <thead>
                        <tr className='border border-gray-300 h-[52px]'>
                            <th className='w-1/5 bg-background-secondary text-left pl-5 font-semibold'>
                                Pools
                            </th>
                            <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                Asset
                            </th>
                            <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                Total Supply
                            </th>
                            <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                Supply APY
                            </th>
                            <th className='w-1/5 bg-background-secondary text-left font-semibold'>
                                Your Wallet
                            </th>
                            <th className='w-1/5 bg-background-secondary text-left font-semibold' />
                        </tr>
                    </thead>
                    <tbody data-testid='assets-to-lend-table'>{getContent()}</tbody>
                </table>
            </div>
        );
    }

    return (
        <div className='w-full mt-[52px]'>
            <div className='flex items-center justify-between'>
                <h2 className='pl-5 pb-5 text-lg font-semibold'>Assets to Supply</h2>
                <div className='flex items-center gap-7 px-6 pb-6'>
                    <span className='text-gray-500 text-md'>
                        We provide personalised pools upon request
                    </span>
                    <Button
                        variant={ButtonVariant.Ghost}
                        className='flex items-center gap-2'
                        name={'lend-request-personalised-pool'}
                        onClick={handleClick}
                    >
                        <ArrowIcon /> Request personalised pool
                    </Button>
                </div>
            </div>

            {content}
        </div>
    );
});
